import { useMemo, useRef, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { ChevronDown, MapPin, Search, X } from "lucide-react";
import { ISTANBUL_AREAS, OTHER_AREA_VALUE, type AreaOption } from "@/data/istanbulAreas";

interface Props {
  value: string;          // canonical (e.g. "Şişli") OR free-text when "Other" picked
  otherText: string;      // when value === OTHER_AREA_VALUE
  onChange: (value: string, otherText: string) => void;
  required?: boolean;
  className?: string;
  placeholder?: string;
  allowEmpty?: boolean;   // for filters; adds an "All areas" option
  emptyLabel?: string;
}

const norm = (s: string) =>
  s.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");

export const AreaPicker = ({
  value, otherText, onChange, required, className, placeholder, allowEmpty, emptyLabel,
}: Props) => {
  const { t, i18n } = useTranslation();
  const lang = (i18n.language || "en").slice(0, 2);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const click = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", click);
    return () => document.removeEventListener("mousedown", click);
  }, []);

  const labelOf = (a: AreaOption) =>
    lang === "ar" ? a.labelAr : lang === "tr" ? a.labelTr : a.labelEn;

  const filtered = useMemo(() => {
    const q = norm(query.trim());
    if (!q) return ISTANBUL_AREAS;
    return ISTANBUL_AREAS.filter((a) =>
      norm(a.value).includes(q) ||
      norm(a.labelEn).includes(q) ||
      norm(a.labelTr).includes(q) ||
      a.labelAr.includes(query.trim()),
    );
  }, [query]);

  const european = filtered.filter((a) => a.side === "european");
  const asian = filtered.filter((a) => a.side === "asian");

  const selectedLabel = (() => {
    if (!value) return placeholder ?? t("field.area.placeholder");
    if (value === OTHER_AREA_VALUE) return `${t("field.area.other")}${otherText ? ` — ${otherText}` : ""}`;
    const found = ISTANBUL_AREAS.find((a) => a.value === value);
    return found ? labelOf(found) : value;
  })();

  const select = (v: string) => {
    onChange(v, v === OTHER_AREA_VALUE ? otherText : "");
    setOpen(false);
    setQuery("");
  };


  return (
    <div className={className}>
      <div ref={wrapRef} className="relative">
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className="flex w-full items-center justify-between gap-2 rounded-md border border-border bg-background px-3 py-2 text-start text-sm focus:border-primary focus:outline-none"
        >
          <span className="inline-flex items-center gap-2 truncate">
            <MapPin className="h-4 w-4 text-muted-foreground" />
            <span className={value ? "" : "text-muted-foreground"}>{selectedLabel}</span>
          </span>
          <ChevronDown className="h-4 w-4 text-muted-foreground" />
        </button>
        {open && (
          <div className="absolute z-50 mt-1 max-h-80 w-full overflow-hidden rounded-md border border-border bg-popover shadow-premium">
            <div className="border-b border-border p-2">
              <div className="flex items-center gap-2 rounded-md border border-border bg-background px-2">
                <Search className="h-3.5 w-3.5 text-muted-foreground" />
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder={t("field.area.search")}
                  className="w-full bg-transparent py-2 text-sm focus:outline-none"
                />
                {query && (
                  <button type="button" onClick={() => setQuery("")} aria-label={t("action.cancel")}>
                    <X className="h-3.5 w-3.5 text-muted-foreground" />
                  </button>
                )}
              </div>
            </div>
            <div className="max-h-60 overflow-y-auto overscroll-contain py-1 [-webkit-overflow-scrolling:touch]">
              {allowEmpty && (
                <button type="button" onClick={() => select("")}
                  className="block w-full min-h-[44px] px-3 py-2 text-start text-sm hover:bg-secondary touch-manipulation">
                  {emptyLabel ?? t("field.area.placeholder")}
                </button>
              )}
              {european.length > 0 && (
                <>
                  <div className="px-3 py-1 text-[11px] font-bold uppercase text-muted-foreground">{t("field.area.european")}</div>
                  {european.map((a) => (
                    <button key={a.value} type="button" onClick={() => select(a.value)}
                      className={`block w-full min-h-[44px] px-3 py-2 text-start text-sm hover:bg-secondary touch-manipulation ${value === a.value ? "bg-primary/10 font-bold" : ""}`}>
                      {labelOf(a)}{a.parent ? <span className="ms-1 text-xs text-muted-foreground">({a.parent})</span> : null}
                    </button>
                  ))}
                </>
              )}
              {asian.length > 0 && (
                <>
                  <div className="px-3 py-1 text-[11px] font-bold uppercase text-muted-foreground">{t("field.area.asian")}</div>
                  {asian.map((a) => (
                    <button key={a.value} type="button" onClick={() => select(a.value)}
                      className={`block w-full min-h-[44px] px-3 py-2 text-start text-sm hover:bg-secondary touch-manipulation ${value === a.value ? "bg-primary/10 font-bold" : ""}`}>
                      {labelOf(a)}{a.parent ? <span className="ms-1 text-xs text-muted-foreground">({a.parent})</span> : null}
                    </button>
                  ))}
                </>
              )}
              <div className="my-1 border-t border-border" />
              <button type="button" onClick={() => select(OTHER_AREA_VALUE)}
                className={`block w-full min-h-[44px] px-3 py-2 text-start text-sm hover:bg-secondary touch-manipulation ${value === OTHER_AREA_VALUE ? "bg-primary/10 font-bold" : ""}`}>
                {t("field.area.other")}
              </button>
            </div>
          </div>
        )}
      </div>

      {value === OTHER_AREA_VALUE && (
        <input
          value={otherText}
          onChange={(e) => onChange(OTHER_AREA_VALUE, e.target.value)}
          required={required}
          placeholder={t("field.area.specifyOther")}
          className="mt-2 w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none"
        />
      )}
    </div>
  );
};

// Resolve area picker state into the canonical string stored in DB.
export const resolveArea = (value: string, otherText: string) =>
  value === OTHER_AREA_VALUE ? otherText.trim() : value.trim();
